import React, { useEffect, useState } from "react";

import {
  SearchableDropdown,
  SearchableMultiSelect,
} from "../../../../components/ui";
import { MetricTiles } from "../../../../components/ui";
import ProjectSectionTitle from "../ProjectSectionTitle";
import OverviewMetricsRangePicker from "./OverviewMetricsRangePicker";
import ChartGranularityToggle from "./charts/ChartGranularityToggle";
import {
  countOverviewChartWeeks,
  getDefaultOverviewChartGranularity,
} from "./charts/chartsHelpers";
import {
  formatOverviewMetricsRangeTitle,
  getOverviewMetricsRangeSpanDays,
  OVERVIEW_METRICS_TYPE_ALL,
} from "./metricsHelpers";

/**
 * Render a full-width overview summary card with metric tiles and a chart.
 *
 * Type control is either multi-select (``typeKeys``) or single dropdown (``typeKey``).
 *
 * @param {Object} props - Component props.
 * @param {string} props.title - Card title.
 * @param {string} props.subtitle - Card subtitle.
 * @param {Array<{value: string|number, label: string, accent?: string, valueClassName?: string, labelClassName?: string, className?: string}>} props.metrics
 *   Summary metric tiles (shared ``MetricTiles`` shape).
 * @param {React.ReactNode} props.chart - Chart body for the card.
 * @param {Array<Object>} [props.chartDays=[]] - Day rows used for default Daily/Weekly.
 * @param {string} [props.error] - Optional error message under the metrics.
 * @param {string[]} [props.typeKeys] - Multi-select Type values (empty = All).
 * @param {string} [props.typeKey] - Single-select Type value.
 * @param {(next: string[]|string) => void} props.onTypeChange - Type change handler.
 * @param {Array<{value: string, label: string}>} props.typeOptions - Type choices.
 * @param {string} props.typeAriaLabel - Accessible label for the type select.
 * @param {{key: string, start: string|null, end: string|null}} props.range - Active range selection.
 * @param {(selection: {key: string, start: string|null, end: string|null}) => void} props.onRangeChange
 * @param {Array<{start: string, end: string, label: string}>} [props.startSuggestions=[]] Full-month chips.
 * @param {string} props.rangeAriaLabel - Accessible label for the range control.
 * @param {{start?: string|null, end?: string|null}|null} [props.entireProjectBounds=null]
 *   Stored Entire Project bounds for title/label matching.
 * @returns {JSX.Element}
 */
const ProjectSummaryCard = ({
  title,
  subtitle,
  metrics,
  chart,
  chartDays = [],
  error,
  typeKeys,
  typeKey,
  onTypeChange,
  typeOptions,
  typeAriaLabel,
  range,
  onRangeChange,
  startSuggestions = [],
  rangeAriaLabel,
  entireProjectBounds = null,
}) => {
  const useMultiType = Array.isArray(typeKeys);
  const [granularity, setGranularity] = useState(() =>
    getDefaultOverviewChartGranularity(range, chartDays)
  );

  // Reset Daily/Weekly when the window length changes (not when it only slides).
  const rangeSpanDays = getOverviewMetricsRangeSpanDays(range);
  const usesDataSpanForGranularity = !range?.start || !range?.end;
  const dataWeekCount = usesDataSpanForGranularity
    ? countOverviewChartWeeks(chartDays)
    : null;
  useEffect(() => {
    setGranularity(getDefaultOverviewChartGranularity(range, chartDays));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeSpanDays, dataWeekCount]);

  const chartWithGranularity =
    chart && React.isValidElement(chart)
      ? React.cloneElement(chart, { granularity })
      : chart;
  const rangeTitle = formatOverviewMetricsRangeTitle(range, entireProjectBounds);

  return (
    <section className="rounded-xl border border-gray-100 bg-gray-100 px-3 py-2 shadow-sm shadow-gray-300">
      <div className="mb-2 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <ProjectSectionTitle className="flex min-w-0 items-baseline">
            <span className="shrink-0">{title}</span>
            {rangeTitle ? (
              <span className="ml-1.5 min-w-0 truncate text-base font-medium text-gray-400">
                ({rangeTitle})
              </span>
            ) : null}
          </ProjectSectionTitle>
          <p className="text-sm text-gray-500">{subtitle}</p>
        </div>
        <div className="flex shrink-0 flex-wrap items-end justify-end gap-2">
          {/* Fixed Type width so Mode + date range align across Worker Hours / Concrete. */}
          {useMultiType ? (
            <SearchableMultiSelect
              label="Type"
              value={typeKeys}
              onChange={onTypeChange}
              options={typeOptions}
              emptyPlaceholder="All"
              ariaLabel={typeAriaLabel}
              className="w-48"
              inputClassName="h-[34px] items-center overflow-hidden rounded-md px-2.5 py-0"
            />
          ) : (
            <SearchableDropdown
              label="Type"
              value={typeKey}
              onChange={(next) =>
                onTypeChange(next ?? OVERVIEW_METRICS_TYPE_ALL)
              }
              options={typeOptions}
              ariaLabel={typeAriaLabel}
              clearable={false}
              searchWhenOverflow
              className="w-48"
              inputClassName="rounded-md px-2.5 py-1 max-h-[34px] pr-9"
            />
          )}
          <ChartGranularityToggle
            value={granularity}
            onChange={setGranularity}
          />
          <OverviewMetricsRangePicker
            value={range}
            onChange={onRangeChange}
            granularity={granularity}
            startSuggestions={startSuggestions}
            ariaLabel={rangeAriaLabel}
          />
        </div>
      </div>

      <MetricTiles metrics={metrics} emphasizeHero className="mt-2" />

      {error ? (
        <p className="mt-2 text-sm text-red-600">{error}</p>
      ) : null}

      {chartWithGranularity}
    </section>
  );
};

export default ProjectSummaryCard;
